// src/pages/markets/board/state/useBoardProfiles.ts
//
// Both profile collections and every write to them. Nothing above this touches Firestore.

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetIdentity } from 'react-admin';
import {
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    setDoc,
    updateDoc,
} from 'firebase/firestore';
import {
    MAX_USER_PROFILES,
    PROFILE_VERSION,
    type BoardProfile,
    type ProfileColumn,
    type ProfileRef,
} from './profileTypes';
import { orderByIds, orderShared } from './profileOrder';

const SHARED = 'board_profiles';

function rows(snap: { docs: { id: string; data: () => unknown }[] }): BoardProfile[] {
    return snap.docs.map(d => ({ ...(d.data() as BoardProfile), id: d.id }));
}

export interface UseBoardProfilesResult {
    shared: BoardProfile[];
    personal: BoardProfile[];
    isLoaded: boolean;
    /** The lists reflect a read that actually succeeded — not merely one that settled. */
    isSuccess: boolean;
    atCap: boolean;
    create: (name: string, columns: ProfileColumn[]) => Promise<ProfileRef>;
    rename: (id: string, name: string) => Promise<void>;
    saveColumns: (id: string, columns: ProfileColumn[]) => Promise<void>;
    remove: (ref: ProfileRef) => Promise<void>;
    promote: (id: string, name: string, description: string) => Promise<void>;
    saveSharedOrder: (ids: string[]) => Promise<void>;
}

// The personal order arrives from the caller rather than from usePreferences
// here, so this document is read and written through one door, not two.
export function useBoardProfiles(
    profileOrder: readonly string[],
): UseBoardProfilesResult {
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const userId = identity?.id ? String(identity.id) : null;
    const qc = useQueryClient();
    const queryKey = ['board_profiles', userId ?? ''] as const;

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            const db = getFirestore();
            const [sharedSnap, personalSnap] = await Promise.all([
                getDocs(collection(db, SHARED)),
                getDocs(collection(db, 'users', userId as string, 'board_profiles')),
            ]);
            return { shared: rows(sharedSnap), personal: rows(personalSnap) };
        },
        enabled: Boolean(userId),
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const shared = useMemo(() => orderShared(query.data?.shared ?? []), [query.data]);
    const personal = useMemo(
        () => orderByIds(query.data?.personal ?? [], profileOrder),
        [query.data, profileOrder],
    );
    const atCap = personal.length >= MAX_USER_PROFILES;

    // doc() takes its auto-id branch only on a one-argument call; passing an
    // explicit undefined is a two-argument call and throws.
    const personalRef = useCallback(
        (id?: string) => {
            const col = collection(getFirestore(), 'users', userId as string, 'board_profiles');
            return id === undefined ? doc(col) : doc(col, id);
        },
        [userId],
    );

    const invalidate = useCallback(() => qc.invalidateQueries({ queryKey }), [qc, queryKey]);

    const create = useCallback(
        async (name: string, columns: ProfileColumn[]) => {
            if (!userId) throw new Error('no_user');
            if (personal.length >= MAX_USER_PROFILES) throw new Error('profile_limit');
            const ref = personalRef();
            const now = new Date().toISOString();
            await setDoc(ref, {
                version: PROFILE_VERSION,
                name,
                description: null,
                owner_uid: userId,
                scope: 'user',
                promoted_from: null,
                created_at: now,
                updated_at: now,
                columns,
            });
            await invalidate();
            return { scope: 'user', id: ref.id } as ProfileRef;
        },
        [invalidate, personal.length, personalRef, userId],
    );

    const rename = useCallback(
        async (id: string, name: string) => {
            await setDoc(
                personalRef(id),
                { name, updated_at: new Date().toISOString() },
                { merge: true },
            );
            await invalidate();
        },
        [invalidate, personalRef],
    );

    const saveColumns = useCallback(
        async (id: string, columns: ProfileColumn[]) => {
            await setDoc(
                personalRef(id),
                { columns, updated_at: new Date().toISOString() },
                { merge: true },
            );
            await invalidate();
        },
        [invalidate, personalRef],
    );

    const remove = useCallback(
        async (ref: ProfileRef) => {
            const target =
                ref.scope === 'shared'
                    ? doc(collection(getFirestore(), SHARED), ref.id)
                    : personalRef(ref.id);
            await deleteDoc(target);
            await invalidate();
        },
        [invalidate, personalRef],
    );

    // A promotion is a copy. The personal original keeps its own life from here.
    const promote = useCallback(
        async (id: string, name: string, description: string) => {
            if (!userId) throw new Error('no_user');
            const source = personal.find(p => p.id === id);
            if (!source) throw new Error('no_profile');
            const existing = shared.find(
                p => p.name.trim().toLowerCase() === name.trim().toLowerCase(),
            );
            const sharedCol = collection(getFirestore(), SHARED);
            const target = existing ? doc(sharedCol, existing.id) : doc(sharedCol);
            const now = new Date().toISOString();
            await setDoc(target, {
                version: PROFILE_VERSION,
                name,
                description,
                owner_uid: userId,
                scope: 'shared',
                promoted_from: id,
                created_at: existing?.created_at ?? now,
                updated_at: now,
                columns: source.columns,
                // The whole document is rewritten, so an order the admins already
                // gave this profile has to be carried across by hand. Absent, not
                // undefined: Firestore rejects an explicit undefined.
                ...(existing?.order !== undefined ? { order: existing.order } : {}),
            });
            await invalidate();
        },
        [invalidate, personal, shared, userId],
    );

    // Only the order field, ever: this must not become a second way to edit a
    // shared profile's contents. updateDoc rather than a merging setDoc, because
    // the second would CREATE a profile deleted between the drag and the save.
    const saveSharedOrder = useCallback(
        async (ids: string[]) => {
            const col = collection(getFirestore(), SHARED);
            const live = new Set(shared.map(p => p.id));
            await Promise.all(
                ids
                    .filter(id => live.has(id))
                    .map((id, index) => updateDoc(doc(col, id), { order: index })),
            );
            await invalidate();
        },
        [invalidate, shared],
    );

    return useMemo(
        () => ({
            shared,
            personal,
            isLoaded: !userId ? !identityLoading : query.isSuccess || query.isError,
            isSuccess: query.isSuccess,
            atCap,
            create,
            rename,
            saveColumns,
            remove,
            promote,
            saveSharedOrder,
        }),
        [
            shared,
            personal,
            userId,
            identityLoading,
            query.isSuccess,
            query.isError,
            atCap,
            create,
            rename,
            saveColumns,
            remove,
            promote,
            saveSharedOrder,
        ],
    );
}
